import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";
import {
  formatApiDate,
  formatDisplayDateTime,
  getUsers,
  getUserStats,
  locationFilterParams,
} from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminViewAction from "./SuperAdminViewAction";
import {
  getUrlFilterOverrides,
  hasSubscriptionListFilter,
  hasUrlListFilters,
  shouldApplyClientListFilter,
} from "./utils/dashboardNavigation";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const SUBSCRIPTION_OPTIONS = [
  { value: "", label: "All" },
  { value: "yes", label: "Has subscription" },
  { value: "no", label: "No subscription" },
  { value: "active", label: "Active plan" },
  { value: "expired", label: "Expired subscription" },
];

const PROFILE_OPTIONS = [
  { value: "", label: "All" },
  { value: "trial", label: "Trial" },
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
];

const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };
const filterFormControlSx = { minWidth: { xs: "100%", sm: 220 }, width: "100%" };
const filterMenuProps = {
  PaperProps: { sx: { maxHeight: 320, minWidth: 240, mt: 0.5 } },
};

function FilterSelect({ label, labelId, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small" sx={filterFormControlSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        displayEmpty
        notched
        MenuProps={filterMenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "all"} value={o.value}>
            {o.value ? o.label : <em>{o.label}</em>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterSectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

const TABLE_HEADERS = [
  "S.No",
  "Name",
  "Email",
  "Mobile",
  "Venue",
  "City",
  "Profile",
  "Status",
  "Subscription",
  "Registered",
  "Action",
];

const EMPTY_LIST_FILTERS = {
  status: "",
  subscription: "",
  profile: "",
  subscription_expiry: "",
  subscription_purchased_today: "",
};

const STAT_CARDS = [
  { key: "total", label: "Total Users", filter: { ...EMPTY_LIST_FILTERS } },
  { key: "active_count", label: "Active Users", filter: { ...EMPTY_LIST_FILTERS, status: "active" } },
  { key: "inactive_count", label: "Inactive Users", filter: { ...EMPTY_LIST_FILTERS, status: "inactive" } },
  {
    key: "with_subscription",
    label: "With Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "yes" },
  },
  {
    key: "without_subscription",
    label: "Without Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "no" },
  },
  {
    key: "active_subscription",
    label: "Active Subscription",
    filter: { ...EMPTY_LIST_FILTERS, subscription: "active" },
  },
];

const PROFILE_CARDS = [
  { key: "Premium", label: "Premium", filter: { ...EMPTY_LIST_FILTERS, profile: "premium" } },
  { key: "Trial", label: "Trial", filter: { ...EMPTY_LIST_FILTERS, profile: "trial" } },
  { key: "Free", label: "Free", filter: { ...EMPTY_LIST_FILTERS, profile: "free" } },
];

const cardPaperSx = (selected) => ({
  p: 2,
  textAlign: "center",
  cursor: "pointer",
  transition: "box-shadow 0.2s, transform 0.15s",
  "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
  ...(selected
    ? { outline: "3px solid", outlineColor: "common.white", boxShadow: 6 }
    : {}),
});

function getTableTitle(selectedCard) {
  const match = [...STAT_CARDS, ...PROFILE_CARDS].find((c) => c.key === selectedCard);
  if (match && selectedCard !== "total") return match.label;
  if (selectedCard === "subscription_purchased_today") return "Today Subscriptions";
  if (selectedCard?.startsWith("expiry:")) {
    const period = selectedCard.replace("expiry:", "").replace(/_/g, " ");
    return `Subscription expiry — ${period}`;
  }
  return "All Users";
}

function matchesListFilters(row, filters = {}) {
  if (filters.status) {
    const rowStatus = String(row.status || "").toLowerCase();
    if (filters.status === "active" && rowStatus !== "active") return false;
    if (filters.status === "inactive" && rowStatus === "active") return false;
  }
  if (filters.profile) {
    if (String(row.profile || "").toLowerCase() !== String(filters.profile).toLowerCase()) return false;
  }
  if (filters.subscription === "yes" && !row.has_subscription) return false;
  if (filters.subscription === "no" && row.has_subscription) return false;
  if (filters.subscription === "active" && !row.is_subscription_active) return false;
  return true;
}

function profileChipColor(profile) {
  if (profile === "Premium") return "success";
  if (profile === "Trial") return "warning";
  return "default";
}

function ProfileBadge({ profile }) {
  if (!profile) return "—";
  return <Chip label={profile} size="small" color={profileChipColor(profile)} />;
}

function StatusBadge({ status }) {
  if (!status) return "—";
  const isActive = String(status).toLowerCase() === "active";
  return (
    <Chip
      label={status}
      size="small"
      color={isActive ? "success" : "default"}
      variant={isActive ? "filled" : "outlined"}
    />
  );
}

function SubscriptionIndicator({ hasSubscription, isActive }) {
  const subscribed = hasSubscription === true || hasSubscription === "true";
  const active = isActive === true || isActive === "true";
  const label = !subscribed ? "No subscription" : active ? "Paid — Active" : "Subscription — Inactive";
  return (
    <Chip
      label={label}
      size="small"
      color={!subscribed ? "default" : active ? "success" : "warning"}
      variant={active ? "filled" : "outlined"}
    />
  );
}

export default function VMSuperAdminUsers() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { adminScope, locationLocked } = useAdminPanel();
  const loc = useLocationCascading();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [status, setStatus] = useState("");
  const [subscription, setSubscription] = useState("");
  const [profile, setProfile] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState("total");

  const listRequestSeq = useRef(0);
  const activeListFiltersRef = useRef({ ...EMPTY_LIST_FILTERS });
  const persistedLocationRef = useRef(null);

  const buildBaseFilters = useCallback(
    (overrides = {}) => {
      const effectiveSearch = overrides.search !== undefined ? overrides.search : searchKey;
      const effectiveFromDate = overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
      const effectiveToDate = overrides.toDate !== undefined ? overrides.toDate : toDate;
      const effectiveLoc = overrides.loc !== undefined ? overrides.loc : loc;
      const skipRegistrationDates = hasSubscriptionListFilter({
        ...activeListFiltersRef.current,
        ...overrides,
      });
      const locationParams =
        overrides.locationOverride !== undefined
          ? overrides.locationOverride
          : persistedLocationRef.current || locationFilterParams(effectiveLoc, adminScope, { locked: locationLocked });

      const params = { ...locationParams };
      if (!skipRegistrationDates) {
        const from = formatApiDate(effectiveFromDate);
        const to = formatApiDate(effectiveToDate);
        if (from) params.from = from;
        if (to) params.to = to;
      }
      if (effectiveSearch.trim()) params.search = effectiveSearch.trim();
      return params;
    },
    [fromDate, toDate, searchKey, loc, adminScope, locationLocked]
  );

  const buildListFilters = useCallback(
    (pageOverride, overrides = {}) => {
      const merged = { ...activeListFiltersRef.current, ...overrides };
      const effectiveStatus = merged.status !== undefined ? merged.status : status;
      const effectiveSubscription = merged.subscription !== undefined ? merged.subscription : subscription;
      const effectiveProfile = merged.profile !== undefined ? merged.profile : profile;

      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: overrides.no_of_rows !== undefined ? overrides.no_of_rows : rowsPerPage,
        ...buildBaseFilters(overrides),
      };
      if (effectiveStatus) params.status = effectiveStatus;
      if (effectiveProfile) params.profile = effectiveProfile;
      if (effectiveSubscription === "yes" || effectiveSubscription === "no") {
        params.has_subscription = effectiveSubscription;
      } else if (effectiveSubscription === "active" || effectiveSubscription === "expired") {
        params.subscription = effectiveSubscription;
      }
      if (merged.subscription_expiry) params.subscription_expiry = merged.subscription_expiry;
      if (merged.subscription_purchased_today) {
        params.subscription_purchased_today = merged.subscription_purchased_today;
      }
      return params;
    },
    [page, rowsPerPage, status, subscription, profile, buildBaseFilters]
  );

  const loadList = useCallback(
    async (pageOverride, overrides = {}) => {
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);

      const mergedFilters = { ...activeListFiltersRef.current, ...overrides };
      const filters = buildListFilters(pageOverride, overrides);
      try {
        const listRes = await getUsers(filters);
        if (seq !== listRequestSeq.current) return;

        const rawRows = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
        const filteredRows = shouldApplyClientListFilter(mergedFilters)
          ? rawRows.filter((row) => matchesListFilters(row, mergedFilters))
          : rawRows;

        setRows(filteredRows);
        setTotalRecords(listRes.data?.totalCount ?? filteredRows.length);
      } catch {
        if (seq !== listRequestSeq.current) return;
        setRows([]);
        setTotalRecords(0);
        toast.info("Users API unavailable.");
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [buildListFilters]
  );

  const loadAll = useCallback(
    async (pageOverride, overrides = {}) => {
      const seq = ++listRequestSeq.current;
      setLoading(true);
      setRows([]);

      const mergedFilters = { ...activeListFiltersRef.current, ...overrides };
      try {
        const [statsRes, listRes] = await Promise.all([
          getUserStats(buildBaseFilters(overrides)),
          getUsers(buildListFilters(pageOverride, overrides)),
        ]);
        if (seq !== listRequestSeq.current) return;

        setStats(statsRes.data?.data || null);
        const rawRows = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
        const filteredRows = shouldApplyClientListFilter(mergedFilters)
          ? rawRows.filter((row) => matchesListFilters(row, mergedFilters))
          : rawRows;
        setRows(filteredRows);
        setTotalRecords(listRes.data?.totalCount ?? filteredRows.length);
      } catch {
        if (seq !== listRequestSeq.current) return;
        setStats(null);
        setRows([]);
        setTotalRecords(0);
        toast.info("Users API unavailable.");
      } finally {
        if (seq === listRequestSeq.current) setLoading(false);
      }
    },
    [buildBaseFilters, buildListFilters]
  );

  const applyCardFilter = (card) => {
    const { status: nextStatus, subscription: nextSubscription, profile: nextProfile } = card.filter;
    activeListFiltersRef.current = { ...card.filter };
    setStatus(nextStatus);
    setSubscription(nextSubscription);
    setProfile(nextProfile);
    setSelectedCard(card.key);
    setPage(0);
    loadList(0, activeListFiltersRef.current);
  };

  const handleReset = () => {
    activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS };
    persistedLocationRef.current = null;
    setFromDate(null);
    setToDate(null);
    setSearchKey("");
    setStatus("");
    setSubscription("");
    setProfile("");
    setSelectedCard("total");
    setPage(0);
    if (!locationLocked) {
      loc.resetLocation();
    }
    const resetOverrides = {
      ...EMPTY_LIST_FILTERS,
      search: "",
      fromDate: null,
      toDate: null,
      locationOverride: locationLocked
        ? locationFilterParams(loc, adminScope, { locked: true })
        : {},
    };
    loadAll(0, resetOverrides);
  };

  useEffect(() => {
    const urlOverrides = getUrlFilterOverrides(searchParams);
    if (hasUrlListFilters(searchParams)) {
      activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS, ...urlOverrides.listFilters };
      persistedLocationRef.current =
        Object.keys(urlOverrides.locationOverride || {}).length > 0
          ? urlOverrides.locationOverride
          : null;
      setStatus(urlOverrides.listFilters.status || "");
      setSubscription(urlOverrides.listFilters.subscription || "");
      setProfile(urlOverrides.listFilters.profile || "");
      setFromDate(urlOverrides.fromDate);
      setToDate(urlOverrides.toDate);
      setSearchKey(urlOverrides.search || "");
      setSelectedCard(urlOverrides.selectedCard || "");
      setPage(0);
      loadAll(0, {
        ...urlOverrides.listFilters,
        search: urlOverrides.search,
        fromDate: urlOverrides.fromDate,
        toDate: urlOverrides.toDate,
        locationOverride: urlOverrides.locationOverride,
      });
    } else {
      activeListFiltersRef.current = { ...EMPTY_LIST_FILTERS };
      persistedLocationRef.current = null;
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearchClick = () => {
    persistedLocationRef.current = null;
    activeListFiltersRef.current = {
      ...EMPTY_LIST_FILTERS,
      status,
      subscription,
      profile,
      subscription_expiry: activeListFiltersRef.current.subscription_expiry,
      subscription_purchased_today: activeListFiltersRef.current.subscription_purchased_today,
    };
    setFiltersOpen(false);
    setSelectedCard("");
    setPage(0);
    loadAll(0, activeListFiltersRef.current);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    loadList(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    loadList(0, { no_of_rows: next });
  };

  const openVenuePage = (row) => {
    if (!row?.venue_id) {
      toast.info("This user has no venue yet.");
      return;
    }
    navigate(`/superAdmin/venues/${row.venue_id}`);
  };

  const profileBreakdown = stats?.by_profile || {};

  return (
    <Box>
      <SuperAdminPageHeader
        title="Users"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={loading}>
              Reset
            </Button>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters
            </Button>
          </Stack>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <FilterSectionTitle>Registration date</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Registered from"
                  value={fromDate}
                  onChange={setFromDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Registered to"
                  value={toDate}
                  onChange={setToDate}
                  format="DD/MM/YYYY"
                  minDate={fromDate?.isValid() ? fromDate : undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Location</FilterSectionTitle>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={FILTER_ITEM}
              />
            </Grid>

            <Divider />

            <FilterSectionTitle>User filters</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="users-filter-account-status"
                  label="Account status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  options={STATUS_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="users-filter-subscription"
                  label="Subscription"
                  value={subscription}
                  onChange={(e) => setSubscription(e.target.value)}
                  options={SUBSCRIPTION_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="users-filter-profile"
                  label="Profile"
                  value={profile}
                  onChange={(e) => setProfile(e.target.value)}
                  options={PROFILE_OPTIONS}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Search</FilterSectionTitle>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} lg={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Name, email, mobile, or venue name"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
                <Button variant="contained" onClick={handleSearchClick} disabled={loading} fullWidth>
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {loading && rows.length === 0 && !stats ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
            minHeight: 320,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {stats ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {STAT_CARDS.map((card) => (
              <Grid item xs={6} sm={4} md={2} key={card.key}>
                <Paper
                  role="button"
                  tabIndex={0}
                  onClick={() => applyCardFilter(card)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      applyCardFilter(card);
                    }
                  }}
                  sx={{
                    ...cardPaperSx(selectedCard === card.key),
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                  }}
                >
                  <Typography variant="body2">{card.label}</Typography>
                  <Typography variant="statValue" sx={{ fontWeight: 700 }}>
                    {stats[card.key] ?? 0}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {Object.keys(profileBreakdown).length > 0 ? (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {PROFILE_CARDS.map((card) => (
                <Grid item xs={4} sm={4} md={2} key={card.key}>
                  <Paper
                    role="button"
                    tabIndex={0}
                    onClick={() => applyCardFilter(card)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        applyCardFilter(card);
                      }
                    }}
                    sx={{
                      ...cardPaperSx(selectedCard === card.key),
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    <Typography variant="body2">{card.label}</Typography>
                    <Typography variant="statValue" sx={{ fontWeight: 700 }}>
                      {profileBreakdown[card.key] ?? 0}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </>
      ) : null}


      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1.5 }}>
          {getTableTitle(selectedCard)}
          {!loading && totalRecords > 0 ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({totalRecords} {totalRecords === 1 ? "record" : "records"})
            </Typography>
          ) : null}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {TABLE_HEADERS.map((h) => (
                  <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No users for the current criteria.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow
                    key={`${row.user_id ?? "row"}-${idx}`}
                    hover
                    onClick={() => openVenuePage(row)}
                    sx={{ cursor: row.venue_id ? "pointer" : "default" }}
                  >
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>
                      {row.venue_id ? (
                        <Link
                          component={RouterLink}
                          to={`/superAdmin/venues/${row.venue_id}`}
                          underline="hover"
                          sx={{ color: "primary.main", fontWeight: 700 }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.name ?? "—"}
                        </Link>
                      ) : (
                        row.name ?? "—"
                      )}
                    </TableCell>
                    <TableCell>{row.email ?? "—"}</TableCell>
                    <TableCell>
                      {row.country_code && row.mobile_no
                        ? `+${row.country_code} ${row.mobile_no}`
                        : row.mobile_no ?? "—"}
                    </TableCell>
                    <TableCell>
                      {row.venue_id ? (
                        <Link
                          component={RouterLink}
                          to={`/superAdmin/venues/${row.venue_id}`}
                          underline="hover"
                          sx={{ color: "primary.main", fontWeight: 700 }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.venue_name}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{row.city_name ?? "—"}</TableCell>
                    <TableCell>
                      <ProfileBadge profile={row.profile} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell>
                      <SubscriptionIndicator
                        hasSubscription={row.has_subscription}
                        isActive={row.is_subscription_active}
                      />
                    </TableCell>
                    <TableCell>{formatDisplayDateTime(row.created_at)}</TableCell>
                    <TableCell align="center">
                      <SuperAdminViewAction
                        onClick={(event) => {
                          event.stopPropagation();
                          openVenuePage(row);
                        }}
                        disabled={!row.venue_id}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  disabled={loading}
                  colSpan={TABLE_HEADERS.length}
                  sx={{ border: 0 }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
        </>
      )}
    </Box>
  );
}
